import { Component, computed, Input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DateEx } from '../../../../functions/date-ex';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import { FinancialOverviewResponse } from '../../../../models/api/view/claim/financials/financial-overview/financial-overview-response';
import { BaseComponent } from '../../../base/base.component';
import { DatePickerComponent } from '../../../core/controls/date-picker.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-overview-tab',
  imports: [
    TableComponent,
    ColumnComponent,
    CardComponent,
    DatePickerComponent,
    DetailPanelComponent,
    DetailItemComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-loading-panel [state]="loadingState" loadingStyle="Box">
      <ng-template #loaded>
        <div class="tw-flex tw-gap-4">
          <div class="tw-basis-full tw-min-w-0">
            <app-card label="Current overview">
              <app-table
                [dataSource]="currentOverviewData()"
                [scrollable]="false"
                dataKey="category"
                recordLabel="item"
                [showFilterButton]="false"
                [showFilter]="false"
                [showRecordLabel]="false"
                [showPaginator]="false"
              >
                <app-column name="category" label="Category" [sort]="false" />
                @if (!liability) {
                  <app-column
                    name="indemnity"
                    label="Indemnity"
                    format="c"
                    [sort]="false"
                  />
                  <app-column
                    name="medical"
                    label="Medical"
                    format="c"
                    [sort]="false"
                  />
                } @else {
                  <app-column name="loss" label="Loss" format="c" />
                }
                <app-column
                  name="expense"
                  label="Expense"
                  format="c"
                  [sort]="false"
                />
                @if (!liability) {
                  <app-column
                    name="other"
                    label="Other"
                    format="c"
                    [sort]="false"
                  />
                }
                <app-column
                  name="totals"
                  label="Totals"
                  format="c"
                  [sort]="false"
                />
              </app-table>
              <div class="tw-flex tw-gap-8">
                <div class="tw-basis-1/4">
                  <app-detail-panel
                    [dataSource]="overviewData()"
                    class="tw-w-full"
                  >
                    <app-detail-item
                      label="Last claim reserve date"
                      name="lastReserve"
                      format="d"
                    />
                  </app-detail-panel>
                </div>
              </div>
            </app-card>
          </div>
        </div>
        <div class="tw-flex tw-gap-4 tw-pt-8">
          <div class="tw-basis-full tw-min-w-0">
            <app-card label="Historical overview">
              <div class="tw-basis-full tw-min-w-0 tw-flex tw-justify-end">
                <app-date-picker
                  class="tw-basis-full sm:tw-basis-1/5 tw-min-w-0"
                  [control]="fg.controls.asOfDate"
                  [maxDate]="today"
                  label="As of date"
                />
              </div>
              <app-table
                [dataSource]="historicalOverviewData()"
                [scrollable]="false"
                dataKey="category"
                recordLabel="item"
                [showFilterButton]="false"
                [showFilter]="false"
                [showRecordLabel]="false"
                [showPaginator]="false"
              >
                <app-column name="category" label="Category" [sort]="false" />
                @if (liability) {
                  <app-column
                    name="loss"
                    label="Loss"
                    format="c"
                    [sort]="false"
                  />
                } @else {
                  <app-column
                    name="indemnity"
                    label="Indemnity"
                    format="c"
                    [sort]="false"
                  />
                  <app-column
                    name="medical"
                    label="Medical"
                    format="c"
                    [sort]="false"
                  />
                }
                <app-column
                  name="expense"
                  label="Expense"
                  format="c"
                  [sort]="false"
                />
                @if (!liability) {
                  <app-column
                    name="other"
                    label="Other"
                    format="c"
                    [sort]="false"
                  />
                }
                <app-column
                  name="totals"
                  label="Totals"
                  format="c"
                  [sort]="false"
                />
              </app-table>
            </app-card>
          </div>
        </div>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class OverviewTabComponent extends BaseComponent implements OnInit {
  @Input() level = new FormControl<string>('claim');
  @Input() liability = false;

  private readonly INDEMNITY = 0;
  private readonly MEDICAL = 2;
  private readonly OTHER = 3;
  private readonly EXPENSE = 5;

  private _currentLevel = signal<string>('claim');
  private _financialData = signal<FinancialOverviewResponse | null>(null);
  private _historicalFinancialData = signal<FinancialOverviewResponse | null>(
    null,
  );

  overviewData = computed(() => this._financialData()?.overview || null);
  loadingState: LoadingState = 'Loading';

  private activeDataSet = computed(() => {
    const data = this._financialData();
    const currentLevel = this._currentLevel();

    if (!data?.overview) return null;

    switch (currentLevel) {
      case 'claim':
        return data.overview.claimDataSet;
      case 'Claimant':
        return data.overview.claimantDataSet;
      case 'Event':
        return data.overview.eventDataSet;
      default:
        return data.overview.claimDataSet;
    }
  });

  private historicalActiveDataSet = computed(() => {
    const data = this._historicalFinancialData();
    const currentLevel = this._currentLevel();

    if (!data?.overview) return null;

    switch (currentLevel) {
      case 'claim':
        return data.overview.claimDataSet;
      case 'Claimant':
        return data.overview.claimantDataSet;
      case 'Event':
        return data.overview.eventDataSet;
      default:
        return data.overview.claimDataSet;
    }
  });

  currentOverviewData = computed(() => {
    const dataSet = this.activeDataSet();
    if (!dataSet) return [0, 0, 0, 0, 0, 0];

    return this.calculateCurrentRows(dataSet);
  });

  historicalOverviewData = computed(() => {
    const dataSet = this.historicalActiveDataSet();
    if (!dataSet) return [];

    return this.calculateHistoricalRows(dataSet);
  });

  fg = new FormGroup({
    asOfDate: new FormControl<Date>(new Date()),
  });

  today = new Date();

  async ngOnInit() {
    const claimKey: ClaimKey = getClaimKey();
    this._currentLevel.set(this.level.value || 'claim');
    this.dispose.subs = this.level.valueChanges.subscribe((value) => {
      if (value) {
        this._currentLevel.set(value);
      }
    });

    // removing time part to allow caching for today's date
    const today = DateEx.clearTime(
      this.fg.controls.asOfDate.value || new Date(),
    );

    const [response, historicalResponse] = await Promise.all([
      this.api.financialOverview.get(claimKey),
      this.api.financialOverview.getHistorical(claimKey, today),
    ]);

    this._financialData.set(response);
    this._historicalFinancialData.set(historicalResponse);
    this.loadingState = 'Loaded';

    this.dispose.subs = this.fg.controls.asOfDate.valueChanges.subscribe(
      async (date) => {
        if (date) {
          const historicalResponse =
            await this.api.financialOverview.getHistorical(claimKey, date);
          this._historicalFinancialData.set(historicalResponse);
        }
      },
    );
  }

  private calculateCurrentRows(dataSet: any): any[] {
    if (!dataSet) return [0, 0, 0, 0, 0, 0];

    const incurred = dataSet.reserve || [0, 0, 0, 0, 0, 0];
    const paidToDate = dataSet.paid || [0, 0, 0, 0, 0, 0];
    const netDeductible = dataSet.deductible || [0, 0, 0, 0, 0, 0];
    const scheduled = dataSet.scheduled || [0, 0, 0, 0, 0, 0];
    const recoveries = dataSet.recovery || [0, 0, 0, 0, 0, 0];

    return [
      this.createRow('Incurred', incurred),
      this.createRow('Paid to date', paidToDate),
      this.createRow('Net deductible', netDeductible),
      this.createRow('Reserve', [
        incurred[this.INDEMNITY] -
          paidToDate[this.INDEMNITY] -
          netDeductible[this.INDEMNITY],
        incurred[1] - paidToDate[1] - netDeductible[1],
        incurred[this.MEDICAL] -
          paidToDate[this.MEDICAL] -
          netDeductible[this.MEDICAL],
        incurred[this.OTHER] -
          paidToDate[this.OTHER] -
          netDeductible[this.OTHER],
        incurred[4] - paidToDate[4] - netDeductible[4],
        incurred[this.EXPENSE] -
          paidToDate[this.EXPENSE] -
          netDeductible[this.EXPENSE],
      ]),
      this.createRow('Scheduled', scheduled),
      this.createRow('Available reserve', [
        incurred[this.INDEMNITY] -
          paidToDate[this.INDEMNITY] -
          netDeductible[this.INDEMNITY] -
          scheduled[this.INDEMNITY],
        incurred[1] - paidToDate[1] - netDeductible[1] - scheduled[1],
        incurred[this.MEDICAL] -
          paidToDate[this.MEDICAL] -
          netDeductible[this.MEDICAL] -
          scheduled[this.MEDICAL],
        incurred[this.OTHER] -
          paidToDate[this.OTHER] -
          netDeductible[this.OTHER] -
          scheduled[this.OTHER],
        incurred[4] - paidToDate[4] - netDeductible[4] - scheduled[4],
        incurred[this.EXPENSE] -
          paidToDate[this.EXPENSE] -
          netDeductible[this.EXPENSE] -
          scheduled[this.EXPENSE],
      ]),
      this.createRow('Recoveries', recoveries),
      this.createRow('Net paid', [
        paidToDate[this.INDEMNITY] - recoveries[this.INDEMNITY],
        paidToDate[1] - recoveries[1],
        paidToDate[this.MEDICAL] - recoveries[this.MEDICAL],
        paidToDate[this.OTHER] - recoveries[this.OTHER],
        paidToDate[4] - recoveries[4],
        paidToDate[this.EXPENSE] - recoveries[this.EXPENSE],
      ]),
      this.createRow('Net incurred', [
        incurred[this.INDEMNITY] - recoveries[this.INDEMNITY],
        incurred[1] - recoveries[1],
        incurred[this.MEDICAL] - recoveries[this.MEDICAL],
        incurred[this.OTHER] - recoveries[this.OTHER],
        incurred[4] - recoveries[4],
        incurred[this.EXPENSE] - recoveries[this.EXPENSE],
      ]),
    ];
  }

  private calculateHistoricalRows(dataSet: any): any[] {
    if (!dataSet) return [0, 0, 0, 0, 0, 0];

    const incurred = dataSet.reserve || [0, 0, 0, 0, 0, 0];
    const paidToDate = dataSet.paid || [0, 0, 0, 0, 0, 0];
    const netDeductible = dataSet.deductible || [0, 0, 0, 0, 0, 0];
    const recoveries = dataSet.recovery || [0, 0, 0, 0, 0, 0];

    return [
      this.createRow('Incurred', incurred),
      this.createRow('Paid to date', paidToDate),
      this.createRow('Reserve', [
        incurred[this.INDEMNITY] -
          paidToDate[this.INDEMNITY] -
          netDeductible[this.INDEMNITY],
        incurred[1] - paidToDate[1] - netDeductible[1],
        incurred[this.MEDICAL] -
          paidToDate[this.MEDICAL] -
          netDeductible[this.MEDICAL],
        incurred[this.OTHER] -
          paidToDate[this.OTHER] -
          netDeductible[this.OTHER],
        incurred[4] - paidToDate[4] - netDeductible[4],
        incurred[this.EXPENSE] -
          paidToDate[this.EXPENSE] -
          netDeductible[this.EXPENSE],
      ]),
      this.createRow('Recoveries', recoveries),
      this.createRow('Net paid', [
        paidToDate[this.INDEMNITY] - recoveries[this.INDEMNITY],
        paidToDate[1] - recoveries[1],
        paidToDate[this.MEDICAL] - recoveries[this.MEDICAL],
        paidToDate[this.OTHER] - recoveries[this.OTHER],
        paidToDate[4] - recoveries[4],
        paidToDate[this.EXPENSE] - recoveries[this.EXPENSE],
      ]),
      this.createRow('Net incurred', [
        incurred[this.INDEMNITY] - recoveries[this.INDEMNITY],
        incurred[1] - recoveries[1],
        incurred[this.MEDICAL] - recoveries[this.MEDICAL],
        incurred[this.OTHER] - recoveries[this.OTHER],
        incurred[4] - recoveries[4],
        incurred[this.EXPENSE] - recoveries[this.EXPENSE],
      ]),
    ];
  }

  private createRow(category: string, values: number[]): any {
    const loss = this.liability
      ? values[this.INDEMNITY] +
        values[1] +
        values[this.MEDICAL] +
        values[this.OTHER] +
        values[4]
      : null;
    const indemnity = !this.liability ? values[this.INDEMNITY] : null;
    const medical = !this.liability ? values[this.MEDICAL] : null;
    const other = !this.liability ? values[this.OTHER] : null;
    const expense = values[this.EXPENSE];
    const totals =
      values[this.INDEMNITY] +
      values[1] +
      values[this.MEDICAL] +
      values[this.OTHER] +
      values[4] +
      values[this.EXPENSE];

    return {
      category,
      loss,
      indemnity,
      medical,
      expense,
      other,
      totals,
    };
  }
}
